/**
 * Stops a render-time throw from blanking the app.
 *
 * This exists because of a real failure, and the failure mode is the point: a
 * formatter threw on a value with more decimal places than it expected, React
 * unwound the whole tree, and the user got a **white page that a reload did not
 * fix** — the reload re-fetched the same data and threw again. Nothing on screen
 * said what happened; the only evidence was in the console.
 *
 * Two boundaries are used, at different granularities, and the difference matters:
 *
 * - **Per screen** (`<ErrorBoundary>` inside the route) — the nav, tab bar and
 *   persona switcher survive, so the user can navigate away from the broken screen
 *   instead of being stranded.
 * - **Around the shell** — last resort, catches a failure in the chrome itself.
 *
 * A boundary is not a substitute for fixing the throw. It is what makes the next
 * one diagnosable by whoever hits it rather than only by whoever has the console
 * open.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Names what failed, e.g. "the work-order screen". */
  what: string;
  /** Reset key — changing it clears the error, so navigation recovers. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    // Clearing on a changed reset key is what makes "navigate away" work. Without
    // it the boundary stays latched and every later screen renders the error too.
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is still where the stack belongs — but now it is a supplement to
    // an on-screen message rather than the only record.
    console.error(`[truckbuddy] ${this.props.what} failed to render`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="screen-body" style={{ paddingTop: "var(--space-6)" }}>
        <div className="blueprint panel">
          <i className="corner tl" />
          <i className="corner tr" />
          <i className="corner bl" />
          <i className="corner br" />
          <h5 style={{ margin: 0 }}>Something in {this.props.what} could not be displayed</h5>
          <p className="text-muted" style={{ fontSize: "var(--tb-text-sm)", margin: "var(--space-2) 0 0" }}>
            Something went wrong on this page. Nothing was lost. Try another tab.
          </p>
          <p
            className="mono"
            style={{
              fontSize: "var(--tb-text-xs)",
              margin: "var(--space-3) 0 0",
              padding: "var(--space-2)",
              border: "1px solid var(--color-divider)",
              overflowX: "auto",
            }}
          >
            {error.message}
          </p>
          <p className="text-muted" style={{ fontSize: "var(--tb-text-xs)", margin: "var(--space-2) 0 0" }}>
            Include this line if you report it.
          </p>
        </div>
      </div>
    );
  }
}
